import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MAP_VIEW, LAND, COMMUNES } from "../../communesMap";
import { AREAS } from "../../cities";
import { slugify } from "../../mock";
import { LogoTitle } from "./Common";
import Reveal from "./Reveal";

const TONES = { E: "#1f3a2b", B: "#35553f", D: "#4f6f57", R: "#6c8a71", C: "#8ba48c" };
const LABELS = new Set(["Saint-Malo", "Dinard", "Cancale", "Dinan", "Dol-de-Bretagne", "Combourg", "Pontorson", "Saint-Cast-le-Guildo"]);

function Tooltip({ c }) {
  if (!c) return null;
  return (
    <div data-testid="map-tooltip" className="pointer-events-none absolute -translate-x-1/2 -translate-y-full -mt-3 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#13261c] shadow-lg whitespace-nowrap"
      style={{ left: `${(c.x / MAP_VIEW.w) * 100}%`, top: `${(c.y / MAP_VIEW.h) * 100}%` }}>
      {c.name} <span className="font-script text-[#d9895a] text-base font-normal ml-1">{AREAS[c.area]}</span>
    </div>
  );
}

export function SectorMap({ area = null, className = "rounded-[32px] shadow-[0_40px_80px_-40px_rgba(19,38,28,0.6)]", testid = "sector-map" }) {
  const [hover, setHover] = useState(null);
  const nav = useNavigate();
  const dim = (c) => area && c.area !== area;
  return (
    <div className={`relative overflow-hidden bg-[#13261c] grain ${className}`} data-testid={testid}>
          <svg viewBox={`0 0 ${MAP_VIEW.w} ${MAP_VIEW.h}`} className="w-full h-auto block" role="img" aria-label="Carte des communes desservies">
            <defs>
              <pattern id="waves" width="28" height="14" patternUnits="userSpaceOnUse"><path d="M0 7 Q7 2 14 7 T28 7" fill="none" stroke="#faf7f0" strokeOpacity="0.07" strokeWidth="1.2" /></pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#waves)" />
            <path d={LAND} fill="#efe7d6" stroke="#d9cdb4" strokeWidth="0.8" />
            {COMMUNES.filter((c) => c.d).map((c) => (
              <path key={c.name} d={c.d} data-testid={`map-commune-${slugify(c.name)}`}
                fill={hover?.name === c.name ? "#d9895a" : TONES[c.area]} fillOpacity={dim(c) ? 0.18 : 1} stroke="#efe7d6" strokeWidth="1.1"
                className="cursor-pointer transition-[fill,fill-opacity] duration-200"
                onMouseEnter={() => setHover(c)} onMouseLeave={() => setHover(null)} onClick={() => nav(`/villes/${slugify(c.name)}`)} />
            ))}
            {COMMUNES.filter((c) => !c.d).map((c) => (
              <circle key={c.name} cx={c.x} cy={c.y} r="7" fill={hover?.name === c.name ? "#d9895a" : "#faf7f0"} stroke={TONES[c.area]} strokeWidth="3" className="cursor-pointer"
                data-testid={`map-commune-${slugify(c.name)}`} onMouseEnter={() => setHover(c)} onMouseLeave={() => setHover(null)} onClick={() => nav(`/villes/${slugify(c.name)}`)} />
            ))}
            {COMMUNES.filter((c) => LABELS.has(c.name)).map((c) => (
              <text key={c.name} x={c.x} y={c.y} textAnchor="middle" className="pointer-events-none font-logo font-black" fontSize="17" fill="#faf7f0" stroke="#13261c" strokeWidth="3" paintOrder="stroke">{c.name}</text>
            ))}
          </svg>
          <Tooltip c={hover} />
    </div>
  );
}

export default function CommunesMap() {
  const [area, setArea] = useState(null);
  return (
    <section className="pb-16" data-testid="communes-map">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal className="flex flex-col gap-6 mb-6">
          <LogoTitle as="h2" size="md" title="notre secteur" script="cliquez sur votre commune" />
          <div className="flex flex-wrap gap-2" data-testid="map-filters">
            {[[null, "Tout le secteur"], ...Object.entries(AREAS)].map(([k, label]) => (
              <button key={k || "all"} onClick={() => setArea(k)} data-testid={`map-filter-${k || "all"}`}
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors ${area === k ? "bg-[#13261c] text-white" : "bg-[#f3ede1] text-[#13261c] hover:bg-[#e6dcc8]"}`}>
                {k && <span className="w-2.5 h-2.5 rounded-full" style={{ background: TONES[k] }} />}{label}
              </button>
            ))}
          </div>
        </Reveal>
        <Reveal delay={100}><SectorMap area={area} /></Reveal>
      </div>
    </section>
  );
}
